import { PageHeader, Card, Pill } from "@/components/common";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Sliders, Bot, ShieldCheck } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const MODES = {
  full_auto: { label: "Full Auto", tone: "emerald", desc: "Agent runs without asking." },
  auto_draft: { label: "Auto-Draft", tone: "blue", desc: "Agent drafts; you review in queue." },
  auto_range: { label: "Auto within range", tone: "blue", desc: "Agent acts inside safe limits (±15%)." },
  requires_approval: { label: "Requires Approval", tone: "amber", desc: "Money/public gate — always ask." },
};

const CHOICES = {
  research: ["full_auto", "auto_draft", "requires_approval"],
  design: ["full_auto", "auto_draft", "requires_approval"],
  listing: ["full_auto", "auto_draft", "requires_approval"],
  publish: ["requires_approval", "auto_draft"],
  ads: ["requires_approval", "auto_range"],
  pricing: ["auto_range", "requires_approval", "full_auto"],
};

export default function Settings() {
  const { settings, setTrust } = useSettings();
  const trust = settings?.trust || [];

  const change = async (key, mode) => {
    await setTrust(key, mode);
    toast.success("Autonomy setting saved.");
  };

  return (
    <>
      <PageHeader title="Trust & Autonomy" subtitle="Graduate the agent to more autonomy over time. Money-out and publish stay gated by default." testid="settings-header">
        <Pill tone="emerald"><Bot className="w-3 h-3 mr-1 inline" /> Adaptive</Pill>
      </PageHeader>

      <div className="flex items-center gap-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-2.5">
        <ShieldCheck className="w-4 h-4" /> Publishing and ad spend are protected by human approval by default — loosen them only when you trust a category.
      </div>

      <Card className="divide-y divide-slate-800/70" data-testid="trust-toggles">
        {trust.map((t) => {
          const meta = MODES[t.mode] || MODES.requires_approval;
          const opts = CHOICES[t.key] || Object.keys(MODES);
          return (
            <div key={t.key} className="flex items-center justify-between gap-4 p-5" data-testid={`trust-row-${t.key}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-800/70 border border-slate-700/60 flex items-center justify-center"><Sliders className="w-4 h-4 text-slate-400" /></div>
                <div>
                  <div className="font-medium text-slate-100">{t.name}</div>
                  <div className="text-xs text-slate-500">{meta.desc}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Pill tone={meta.tone}>{meta.label}</Pill>
                <Select value={t.mode} onValueChange={(m) => change(t.key, m)}>
                  <SelectTrigger data-testid={`trust-toggle-${t.key}`} className="w-52 bg-[#0C0F14] border-slate-800 text-slate-100"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#12161F] border-slate-800 text-slate-100">
                    {opts.map((o) => <SelectItem key={o} value={o} className="focus:bg-slate-800">{MODES[o].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        })}
      </Card>
    </>
  );
}
